import { useEffect, useRef, useCallback } from 'react';

// ✅ Auto-detect WebSocket URL (localhost vs production)
const WS_URL = (() => {
  if (window.location.hostname === 'localhost') {
    return 'ws://localhost:3001';
  }
  return process.env.REACT_APP_WS_URL || 'wss://app.alpenscreen.ch/ws';
})();

export const useWebSocket = (clientType, orgId = null, onMessage) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const isCleaningUp = useRef(false);

  const connect = useCallback(() => {
    // Prevent connection if cleaning up or already connected
    if (isCleaningUp.current) {
      console.log('⛔️ Skipping connection (cleanup in progress)');
      return;
    }

    if (wsRef.current?.readyState === WebSocket.CONNECTING ||
        wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('⛔️ WebSocket already connected/connecting');
      return;
    }

    try {
      console.log('🔌 Connecting to WebSocket...', WS_URL);
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
  console.log('✅ WebSocket connected');
  console.log('   Registering as:', clientType, 'ID:', orgId);
  reconnectAttempts.current = 0;

  // ✅ FIX: Different registration for player vs dashboard
  if (clientType === 'player') {
    // For player: orgId is actually the screenUuid
    ws.send(JSON.stringify({
      type: 'register',
      clientType: 'player',
      screenUuid: orgId  // ✅ Send as screenUuid!
    }));
  } else {
    // For dashboard: orgId is the organization ID
    ws.send(JSON.stringify({
      type: 'register',
      clientType: clientType,
      organizationId: orgId
    }));
  }

  // Start heartbeat
  const heartbeatInterval = setInterval(() => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'heartbeat' }));
    }
  }, 30000);

  ws.heartbeatInterval = heartbeatInterval;
};

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Don't log heartbeat and connected messages
          if (message.type !== 'heartbeat_ack' && message.type !== 'connected') {
            console.log('📨 WebSocket message:', message.type, message.data);
          }

          if (onMessage && message.type !== 'heartbeat_ack' && message.type !== 'connected') {
            onMessage(message);
          }
        } catch (error) {
          console.error('❌ Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        // Only log if not during cleanup AND connection was established
        if (!isCleaningUp.current && ws.readyState !== WebSocket.CLOSED) {
          console.error('❌ WebSocket error:', error);
        }
      };

      ws.onclose = (event) => {
        // Only log if not a clean unmount
        if (!isCleaningUp.current) {
          console.log('🔌 WebSocket disconnected');
        }

        // Clear heartbeat
        if (ws.heartbeatInterval) {
          clearInterval(ws.heartbeatInterval);
        }

        // Only attempt reconnect if not cleaning up
        if (!isCleaningUp.current && reconnectAttempts.current < 10) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`🔄 Reconnecting in ${delay}ms...`);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('❌ WebSocket connection error:', error);
    }
  }, [clientType, orgId, onMessage]);

  useEffect(() => {
    isCleaningUp.current = false;
    connect();

    return () => {
      // Silent cleanup
      isCleaningUp.current = true;

      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Close WebSocket properly
      if (wsRef.current) {
        const ws = wsRef.current;

        // Clear heartbeat
        if (ws.heartbeatInterval) {
          clearInterval(ws.heartbeatInterval);
          ws.heartbeatInterval = null;
        }

        // Close connection with proper code (silently)
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, 'Component unmounting');
        }

        wsRef.current = null;
      }

      // Reset reconnect attempts
      reconnectAttempts.current = 0;
    };
  }, [connect]);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('⛔️ Cannot send message: WebSocket not connected');
    }
  }, []);

  return { sendMessage };
};